import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WebSocketContext";
import Sidebar from "@/components/chat/Sidebar";
import ConversationView from "@/components/chat/ConversationView";
import { toast } from "sonner";

export function convMeta(conv, meId, online) {
  if (!conv) return { title: "", isGroup: false, peer: null, onlineNow: false };
  if (conv.type === "group") {
    return { title: conv.name || "Gruppo", isGroup: true, peer: null, onlineNow: false, subtitle: `${conv.members.length} membri` };
  }
  const peer = conv.members.find((m) => m.id !== meId) || conv.members[0];
  return {
    title: peer?.displayName || "Utente",
    isGroup: false,
    peer,
    onlineNow: peer ? online.has(peer.id) : false,
  };
}

export default function ChatApp() {
  const { user } = useAuth();
  const { subscribe, online } = useWS();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [initialSearch, setInitialSearch] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifPerm, setNotifPerm] = useState(false);
  const [appHeight, setAppHeight] = useState("100dvh");
  const [chatScale, setChatScale] = useState(() => Number(localStorage.getItem("relatio_chat_scale")) || 1);
  const changeScale = useCallback((n) => {
    setChatScale(n);
    try { localStorage.setItem("relatio_chat_scale", String(n)); } catch {}
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = null;
    const apply = () => {
      // pin the shell to exactly the visible area so the composer stays just above the keyboard
      setAppHeight(`${Math.round(vv.height)}px`);
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    const onChange = () => { if (raf) cancelAnimationFrame(raf); raf = requestAnimationFrame(apply); };
    apply();
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    return () => { if (raf) cancelAnimationFrame(raf); vv.removeEventListener("resize", onChange); vv.removeEventListener("scroll", onChange); };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((p) => setNotifPerm(p === "granted"));
    } else if ("Notification" in window) {
      setNotifPerm(Notification.permission === "granted");
    }
  }, []);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
  }, []);

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    loadConversations();
    loadUsers();
  }, [loadConversations, loadUsers]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || isMobile || !conversations.length) return;
    restoredRef.current = true;
    try {
      const last = localStorage.getItem("relatio_last_conv");
      if (last && conversations.some((c) => c.id === last)) setActiveId(last);
    } catch {}
  }, [conversations, isMobile]);

  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeId) || null,
    [conversations, activeId]
  );

  // Handle conversation-list level WS events
  useEffect(() => {
    const unsub = subscribe((evt) => {
      if (evt.type === "message") {
        const m = evt.message;
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === m.conversationId);
          if (idx === -1) {
            loadConversations();
            return prev;
          }
          const conv = { ...prev[idx] };
          conv.lastMessage = {
            text: m.text ? m.text.slice(0, 80) : "📎 Allegato",
            senderId: m.senderId,
            createdAt: m.createdAt,
          };
          conv.updatedAt = m.createdAt;
          const isActive = m.conversationId === activeId;
          const fromMe = m.senderId === user.id;
          if (!isActive && !fromMe) conv.unreadCount = (conv.unreadCount || 0) + 1;
          const rest = prev.filter((_, i) => i !== idx);
          if (!isActive && !fromMe && notifPerm && document.hidden) {
            try {
              new Notification("Nuovo aggiornamento", { body: "Nuovo aggiornamento su una scheda", silent: false });
            } catch {}
          }
          return [conv, ...rest];
        });
      } else if (["conversation_new", "conversation_update"].includes(evt.type)) {
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === evt.conversation.id);
          if (idx === -1) return [evt.conversation, ...prev];
          const copy = [...prev];
          copy[idx] = { ...copy[idx], ...evt.conversation };
          return copy;
        });
      } else if (evt.type === "message_edited") {
        loadConversations();
      } else if (evt.type === "conversation_deleted" || evt.type === "conversation_member_removed") {        const cid = evt.conversationId;
        if (evt.type === "conversation_member_removed" && evt.userId !== user.id) return;
        setConversations((prev) => prev.filter((c) => c.id !== cid));
        setActiveId((a) => (a === cid ? null : a));
      }
    });
    return unsub;
  }, [subscribe, activeId, user.id, notifPerm, loadConversations]);

  const openConversation = useCallback((id, searchTerm = null) => {
    setActiveId(id);
    setInitialSearch(searchTerm);
    try { localStorage.setItem("relatio_last_conv", id); } catch {}
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c)));
  }, []);

  const onLeaveConversation = useCallback((id) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setActiveId((a) => (a === id ? null : a));
  }, []);

  const onNewConversation = useCallback((conv) => {
    setConversations((prev) => {
      if (prev.some((c) => c.id === conv.id)) return prev;
      return [conv, ...prev];
    });
    setActiveId(conv.id);
  }, []);

  const onDeleteConversation = useCallback(
    async (id) => {
      try {
        await api.delete(`/conversations/${id}`);
        setConversations((prev) => prev.filter((c) => c.id !== id));
        setActiveId((a) => (a === id ? null : a));
        toast.success("Scheda eliminata");
      } catch (e) {
        toast.error("Impossibile eliminare la scheda");
      }
    },
    []
  );

  const showSidebar = !isMobile || !activeId;
  const showChat = !isMobile || !!activeId;

  return (
    <div style={{ height: appHeight }} className="relative w-full flex overflow-hidden bg-background">
      {showSidebar && (
        <Sidebar
          me={user}
          conversations={conversations}
          users={users}
          online={online}
          activeId={activeId}
          isMobile={isMobile}
          onOpen={openConversation}
          onNewConversation={onNewConversation}
          onGoAdmin={() => navigate("/admin")}
          reloadUsers={loadUsers}
          chatScale={chatScale}
          onSetScale={changeScale}
        />
      )}
      {showChat && (
        <ConversationView
          key={`${activeId || "empty"}:${initialSearch || ""}`}
          me={user}
          conversation={activeConv}
          online={online}
          isMobile={isMobile}
          onBack={() => setActiveId(null)}
          onDelete={onDeleteConversation}
          onLeave={onLeaveConversation}
          users={users}
          onConversationChanged={loadConversations}
          initialSearch={initialSearch}
          chatScale={chatScale}
        />
      )}
    </div>
  );
}
